import { dbCols } from '@cc/schema';
import {
  createWorryTreeMachine,
  generateWorryTreeUmlText,
  WorryTreeContext,
} from '@cc/utils';
import { Create } from '@refinedev/antd';
import {
  useCustomMutation,
  useGetLocale,
  useWarnAboutChange,
} from '@refinedev/core';
import { useActor, useMachine } from '@xstate/react';
import { Button, Card, Input, message, Typography } from 'antd';
import { DiagramImage } from '../../components/DiagramImage';
import qs from 'qs';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useWindowSize } from 'rooks';
import { TherapistActivityReactContext } from '../../App';
import { SelectClient } from '../../components/SelectClient';
import { useClientOptions } from '../../hooks/client-options';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { WorryTree } from '../../schema/be-graphql-generated';
import useStyles from './styles.style';

const { Title } = Typography;

const worryMachine = createWorryTreeMachine({});

export const WorryTreeCreate = () => {
  const { t, i18n } = useTranslation();
  const locale = useGetLocale();
  const currentLocale = locale();
  const location = useLocation();
  const { setWarnWhen } = useWarnAboutChange();

  const { styles } = useStyles();

  const { clientsLoading } = useClientOptions();

  const { clientId: locationClientId, worryTreeId } =
    useMemo(
      () =>
        qs.parse(
          location.search.charAt(0) === '?'
            ? location.search.slice(1)
            : location.search,
        ),
      [location.search],
    ) || {};

  const { innerWidth } = useWindowSize();

  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const [clientId, setClientId] = useState<string | undefined>(
    // E57 (Q-e57g1-2 A rule 4): a create page opened with ?clientId= (Create on a client's tool tab, Use a tool) keeps
    // THAT client; the header bar's filter is the fallback
    (locationClientId as string) ||
      therapistActivityState.context.filterClientId,
  );

  useEffect(() => {
    if (
      clientId &&
      therapistActivityState.context.filterClientId !== clientId
    ) {
      therapistActivitySend({
        type: 'ENTITY_CLIENT_ID_CHANGED',
        clientId,
      });
    }
  }, [clientId]);

  const [worryTreeState, worryTreeSend, worryTreeService] =
    useMachine(worryMachine);

  // the PlantUML text for the finished tree; our API draws it (F-199)
  const [diagramSource, setDiagramSource] = useState('');

  const {
    mutate: createWorryTree,
    data: createWorryTreeData,
    isLoading: createWorryTreeLoading,
    error: createWorryTreeError,
  } = useCustomMutation<{ supportingCycle: WorryTree }>();

  useActivityTracking(
    // @ts-ignore
    createWorryTreeData?.data?._id || `${dbCols.worryTrees}/create`,
  );

  useEffect(() => {
    // @ts-ignore
    if (createWorryTreeData?.data?._id) {
      if (!worryTreeId) {
        therapistActivitySend({
          type: 'CREATED',
          // @ts-ignore
          resource: createWorryTreeData?.data?._id,
        });
      }
    }
  }, [createWorryTreeData]);

  const saveData = async (data: WorryTreeContext) => {
    const res = await createWorryTree({
      url: '',
      method: 'post',
      values: {},
      meta: {
        operation: 'createWorryTree',
        fields: ['_id'],
        variables: {
          input: {
            name: 'input',
            type: 'WorryTreeInput',
            value: {
              data,
              where: {
                clientId,
              },
            },
          },
        },
      },
    });
    message.success(t('shared.worryTree.saveSuccess'));
    setWarnWhen(false);
  };

  useEffect(() => {
    if (worryTreeState.matches('done')) {
      const umlText = generateWorryTreeUmlText(
        worryTreeState.context,
        // @ts-ignore
        t,
        currentLocale,
        (innerWidth || 0) < 850,
      );
      setDiagramSource(umlText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worryTreeState.matches('done'), i18n.language]);

  useEffect(() => {
    if (worryTreeState.matches('done')) {
      saveData(worryTreeState.context);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worryTreeState.matches('done')]);

  useEffect(() => {
    setWarnWhen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worryTreeState.matches('isReal')]);

  return (
    <Create
      title={t('shared.worryTree.title')}
      isLoading={clientsLoading}
      saveButtonProps={{
        style: {
          display: 'none',
        },
      }}
    >
      <div
        className={styles['worry-tree-container']}
        data-testid="cc-create-worry-tree-container"
      >
        <SelectClient clientId={clientId} setClientId={setClientId} />

        {clientId && worryTreeState.matches('worryAbout') ? (
          <div className={styles['fading-container']}>
            <Title level={4}>{t('shared.worryTree.worryAbout')}</Title>
            <Input.TextArea
              data-testid="cc-worry-tree-worry-about"
              id="worryAbout"
              autoSize={{ minRows: 4, maxRows: 10 }}
              value={worryTreeState.context.worryAbout}
              onChange={(e) => {
                worryTreeSend({
                  type: 'WORRY_ABOUT_CHANGED',
                  data: e.target.value,
                });
              }}
              placeholder={t('shared.worryTree.worryAbout')}
              className={styles['worry-tree--input']}
            />

            <Button
              data-testid="cc-worry-tree-about-next-button"
              type="primary"
              size="large"
              disabled={worryTreeState.context.worryAbout.length < 6}
              onClick={() => {
                worryTreeSend('GO_NEXT');
              }}
            >
              {t('resourcesCommon.next')}
            </Button>
          </div>
        ) : null}

        {worryTreeState.matches('isReal') ? (
          <div className={styles['fading-container']}>
            <Title level={4}>{t('shared.worryTree.isReal')}</Title>
            <div className={styles['worry-tree--buttons-container']}>
              <Button
                data-testid="cc-worry-tree-is-real-yes-button"
                type="primary"
                size="large"
                onClick={() => {
                  worryTreeSend({
                    type: 'GO_NEXT',
                    decisionIdx: 0,
                  });
                }}
              >
                {t('resourcesCommon.yes')}
              </Button>
              <Button
                data-testid="cc-worry-tree-is-real-no-button"
                type="primary"
                size="large"
                onClick={() => {
                  worryTreeSend('GO_NEXT');
                }}
              >
                {t('resourcesCommon.no')}
              </Button>
            </div>
          </div>
        ) : null}

        {worryTreeState.matches('isDoable') ? (
          <div className={styles['fading-container']}>
            <Title level={4}>{t('shared.worryTree.isDoable')}</Title>
            <div className={styles['worry-tree--buttons-container']}>
              <Button
                data-testid="cc-worry-tree-is-doable-yes-button"
                type="primary"
                size="large"
                onClick={() => {
                  worryTreeSend({
                    type: 'GO_NEXT',
                    decisionIdx: 0,
                  });
                }}
              >
                {t('resourcesCommon.yes')}
              </Button>
              <Button
                data-testid="cc-worry-tree-is-doable-no-button"
                type="primary"
                size="large"
                onClick={() => {
                  worryTreeSend('GO_NEXT');
                }}
              >
                {t('resourcesCommon.no')}
              </Button>
            </div>
          </div>
        ) : null}

        {worryTreeState.matches('listOptions') ? (
          <div className={styles['fading-container']}>
            <Title level={4}>{t('shared.worryTree.listOptions')}</Title>
            <Input.TextArea
              data-testid="cc-worry-tree-options"
              id="worryAbout"
              autoSize={{ minRows: 4, maxRows: 10 }}
              value={worryTreeState.context.options}
              onChange={(e) => {
                worryTreeSend({
                  type: 'OPTIONS_CHANGED',
                  data: e.target.value,
                });
              }}
              placeholder={t('shared.worryTree.developPlan')}
              className={styles['worry-tree--input']}
            />

            <Button
              data-testid="cc-worry-tree-options-next-button"
              type="primary"
              size="large"
              disabled={worryTreeState.context.options.length < 6}
              onClick={() => {
                worryTreeSend('GO_NEXT');
              }}
            >
              {t('resourcesCommon.next')}
            </Button>
          </div>
        ) : null}

        {worryTreeState.matches('isDoableNow') ? (
          <div className={styles['fading-container']}>
            <Title level={4}>{t('shared.worryTree.isDoableNow')}</Title>
            <div className={styles['worry-tree--buttons-container']}>
              <Button
                data-testid="cc-worry-tree-is-doable-now-yes-button"
                type="primary"
                size="large"
                onClick={() => {
                  worryTreeSend({
                    type: 'GO_NEXT',
                    decisionIdx: 0,
                  });
                }}
              >
                {t('resourcesCommon.yes')}
              </Button>
              <Button
                data-testid="cc-worry-tree-is-doable-now-no-button"
                type="primary"
                size="large"
                onClick={() => {
                  worryTreeSend('GO_NEXT');
                }}
              >
                {t('resourcesCommon.no')}
              </Button>
            </div>
          </div>
        ) : null}

        {worryTreeState.matches('createPlan') ? (
          <div className={styles['fading-container']}>
            <Title level={4}>{t('shared.worryTree.developPlan')}</Title>
            <Input.TextArea
              data-testid="cc-worry-tree-plan"
              id="worryAbout"
              autoSize={{ minRows: 4, maxRows: 10 }}
              value={worryTreeState.context.plan}
              onChange={(e) => {
                worryTreeSend({
                  type: 'PLAN_CHANGED',
                  data: e.target.value,
                });
              }}
              placeholder={t('shared.worryTree.developPlan')}
              className={styles['worry-tree--input']}
            />

            <Button
              data-testid="cc-worry-tree-plan-next-button"
              type="primary"
              size="large"
              disabled={worryTreeState.context.plan.length < 6}
              onClick={() => {
                worryTreeSend('GO_NEXT');
              }}
            >
              {t('resourcesCommon.next')}
            </Button>
          </div>
        ) : null}

        {worryTreeState.matches('immediateAction') ? (
          <div className={styles['fading-container']}>
            <Title level={4}>{t('shared.worryTree.immediateAction')}</Title>
            <Input.TextArea
              data-testid="cc-worry-tree-immediate-action"
              id="worryAbout"
              autoSize={{ minRows: 4, maxRows: 10 }}
              value={worryTreeState.context.immediateAction}
              onChange={(e) => {
                worryTreeSend({
                  type: 'IMMEDIATE_ACTION_CHANGED',
                  data: e.target.value,
                });
              }}
              placeholder={t('shared.worryTree.immediateAction')}
              className={styles['worry-tree--input']}
            />

            <Button
              data-testid="cc-worry-tree-immediate-action-next-button"
              type="primary"
              size="large"
              disabled={worryTreeState.context.immediateAction.length < 6}
              onClick={() => {
                worryTreeSend('GO_NEXT');
              }}
            >
              {t('resourcesCommon.finish')}
            </Button>
          </div>
        ) : null}

        {worryTreeState.matches('switchFocus') ||
        worryTreeState.matches('done') ? (
          <div className={styles['fading-container']}>
            <Title level={4}>
              {t(
                worryTreeState.matches('done')
                  ? 'shared.worryTree.doneTitle'
                  : 'shared.worryTree.refocusTitle',
              )}
            </Title>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 15,
                justifyContent: 'center',
              }}
            >
              {(
                t('shared.worryTree.refocusIdeas', {
                  returnObjects: true,
                }) as {
                  title: string;
                  info: string;
                  mobileExtraInfo?: string;
                  link?: string;
                  browserLink?: string;
                  root?: string;
                }[]
              ).map((item, idx) => {
                return worryTreeState.matches('done') &&
                  worryTreeState.context.selectedActivityIdx !== idx ? null : (
                  <Card
                    data-testid={`cc-worry-tree-refocus-card-${idx}`}
                    key={item.title}
                    hoverable={true}
                    onClick={() => {
                      if (!worryTreeState.matches('done')) {
                        worryTreeSend({
                          type: 'GO_NEXT',
                          decisionIdx: idx,
                        });
                      }
                    }}
                    title={item.title}
                    bodyStyle={{
                      display:
                        worryTreeState.context.selectedActivityIdx !== idx
                          ? 'none'
                          : 'block',
                      maxWidth: (innerWidth || 0) > 400 ? 400 : '100%',
                    }}
                  >
                    <div>{item.info}</div>
                  </Card>
                );
              })}
            </div>
          </div>
        ) : null}
        {worryTreeState.matches('done') && diagramSource && (
          <>
            <Title
              className={styles['worry-tree--image-title']}
              level={4}
              data-testid="cc-worry-tree-done-title"
            >
              {t('shared.worryTree.savedForReference')}
            </Title>
            <div
              style={{
                display: 'grid',
                maxWidth: '100%',
              }}
            >
              <DiagramImage
                source={diagramSource}
                fallbackLines={[
                  worryTreeState.context.worryAbout,
                  worryTreeState.context.options,
                  worryTreeState.context.plan,
                  worryTreeState.context.immediateAction,
                ].flatMap((text) => (text ?? '').split(/\r?\n/))}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100vh',
                  margin: 'auto',
                }}
              />
            </div>
          </>
        )}
      </div>
    </Create>
  );
};
